import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVersions, restoreVersion } from '../api/client.js';
import type { TemplateVersion } from '../types/index.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VersionHistory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  function load() {
    if (!id) return;
    setLoading(true);
    fetchVersions(id)
      .then(setVersions)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }

  useEffect(load, [id]);

  async function handleRestore(versionId: string) {
    if (!id) return;
    setRestoring(versionId);
    try {
      await restoreVersion(id, versionId);
      load();
    } catch (err) {
      alert(`Restore failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRestoring(null);
    }
  }

  if (loading) return <div className="status-msg">Loading versions…</div>;
  if (error) return <div className="status-msg status-msg--error">Error: {error}</div>;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h2>Version History</h2>
          <p className="page__subtitle">Template: <strong>{id}</strong></p>
        </div>
        <div>
          <button className="btn" onClick={() => navigate('/')}>Back</button>
          <button className="btn btn--primary" onClick={() => navigate(`/templates/${id}/edit`)}>
            Edit Active Version
          </button>
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Version ID</th>
            <th>Created</th>
            <th>By</th>
            <th>Comment</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.versionId} className={v.active ? 'tr--active' : ''}>
              <td className="td--mono">{v.versionId}</td>
              <td>{formatDate(v.createdAt)}</td>
              <td>{v.createdBy}</td>
              <td>{v.comment}</td>
              <td>
                {v.active && <span className="badge badge--active">Active</span>}
              </td>
              <td className="td--actions">
                <button
                  className="btn btn--sm btn--ghost"
                  onClick={() =>
                    navigate(
                      `/templates/${id}/edit?versionId=${encodeURIComponent(v.versionId)}`
                    )
                  }
                >
                  View
                </button>
                {!v.active && (
                  <button
                    className="btn btn--sm"
                    disabled={restoring === v.versionId}
                    onClick={() => handleRestore(v.versionId)}
                  >
                    {restoring === v.versionId ? 'Restoring…' : 'Restore'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
